import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { findIndex } from 'lodash-es'
import type { TRootState } from '..'
import {
  Applicant,
  BaseWorksheet,
  CreditApplication,
  CreditApplicationTask,
  CreditTaskComment,
  FullCreditApplication,
  Merchant,
  NormsMessage,
  PreviousApplication,
} from '../../types'
import { Constants, defaultProgramConfig, EAfterNormEffectsEnum } from '../../types/constants'
import { apiStateSelectors } from '../ApiStateStore'

import {
  CreditDecision,
  EApplicantType,
  EContinueFinancingDisableReason,
  ECreditApplicationStatus,
  ECreditApplicationStep,
  EDecision,
  EDocumentStatus,
  EDocumentType,
  EFinancingProgram,
  ELoanPurpose,
  EProgressionStatus,
  EProvince,
  EStaticMerchantIds,
  ETasksStatus,
  ETaskType,
  EWorksheetDecision,
} from '@if/core/types'
import { CreditApplicationReservation } from '../../types/CreditApplicationReservation'
import { CreditDashboardEntry } from '../../types/CreditDashboardEntry'
import { FlinksIncome } from '../../types/FlinksIncome'
import { CreditDecisionHistory, IncomesHistory } from '../../types/IncomesHistory'
import { Refinancing } from '../../types/RefinancingSchema'
import { appSelectors } from '../AppStore'
import { autoRslaWorksheetSelectors } from '../AutoRslaWorksheet/auto-rsla-worksheet-store'
import { autoworksheetSelectors } from '../AutoWorksheet'
import { availableCreditSelectors } from '../AvailableCredit/available-credit-store'
import { b2cWorksheetSelectors } from '../B2cWorksheet/b2c-worksheet-store'
import { creditRslaWorksheetSelectors } from '../CreditRslaWorksheet/credit-rsla-worksheet-store'
import { ifXpressWorksheetSelectors } from '../IfXpressWorksheet/ifxpress-worksheet-store'
import { mechanicalWarrantyWorksheetSelectors } from '../MechanicalWarrantyWorksheet'
import { personalLoanWorksheetSelectors } from '../PersonalLoanWorksheet/personal-loan-worksheet-store'
import { productsWorkSheetSelectors } from '../ProductsWorksheet'

export const creditSlice = createSlice({
  name: 'credit',
  initialState: {
    current: null as CreditApplication | null,
    dashboardEntry: null as CreditDashboardEntry | null,
    merchant: null as Merchant | null,
    list: [] as CreditApplication[],
    taskList: [] as CreditApplicationTask[],
    applicantPreviousApplications: [] as PreviousApplication[],
    coApplicantPreviousApplications: [] as PreviousApplication[],
    incomesHistory: null as IncomesHistory | null,
    lock: null as CreditApplicationReservation | null,
    progressionStatus: null as EProgressionStatus | null,
    decisionHistory: [] as CreditDecisionHistory[],
    flinksIncomes: [] as FlinksIncome[],
    isRush: false,
  },
  reducers: {
    setIncomesHistory: (state, action: PayloadAction<IncomesHistory | null>) => {
      state.incomesHistory = action.payload
    },
    setDecisionHistory: (state, action: PayloadAction<CreditDecisionHistory[]>) => {
      state.decisionHistory = action.payload
    },
    setCurrent: (state, action: PayloadAction<CreditApplication | null>) => {
      state.current = action.payload
    },
    setList: (state, action: PayloadAction<CreditApplication[]>) => {
      state.list = action.payload
    },
    setCurrentCreditApplicationTasks: (state, action: PayloadAction<CreditApplicationTask[]>) => {
      state.taskList = action.payload
    },
    setFlinksIncomes: (state, action: PayloadAction<FlinksIncome[]>) => {
      state.flinksIncomes = action.payload
    },
    setCreditTask: (state, action: PayloadAction<CreditApplicationTask | null>) => {
      if (action.payload === null) return
      const index = findIndex(state.taskList, { id: action.payload.id })
      if (index === -1) {
        action.payload.comments = []
        state.taskList.push(action.payload)
      } else {
        action.payload.comments = state.taskList[index].comments
        state.taskList.splice(index, 1, action.payload)
      }
    },
    setCreditTaskComment: (state, action: PayloadAction<CreditTaskComment | null>) => {
      if (action.payload) {
        state.taskList.find((task) => task.id === action.payload!.taskId)?.comments.unshift(action.payload)
      }
    },
    removeCreditTask: (state, action: PayloadAction<CreditApplicationTask>) => {
      const index = findIndex(state.taskList, { id: action.payload.id })
      state.taskList.splice(index, 1)
    },
    setApplicantPreviousApplications: (state, action: PayloadAction<PreviousApplication[] | null>) => {
      if (action.payload) {
        state.applicantPreviousApplications = action.payload
      }
    },
    setCoapplicantPreviousApplications: (state, action: PayloadAction<PreviousApplication[] | null>) => {
      if (action.payload) {
        state.coApplicantPreviousApplications = action.payload
      }
    },
    setCreditApplicationLock: (state, action: PayloadAction<CreditApplicationReservation | null>) => {
      state.lock = action.payload
    },
    setRefinancing: (state, action: PayloadAction<Refinancing>) => {
      if (state.current) {
        state.current.refinancing = action.payload
      }
    },
    setDashboardEntry: (state, action: PayloadAction<CreditDashboardEntry>) => {
      state.dashboardEntry = action.payload
    },
    setMerchant: (state, action: PayloadAction<Merchant | null>) => {
      state.merchant = action.payload
    },
    setProgressionStatus: (state, action: PayloadAction<EProgressionStatus>) => {
      state.progressionStatus = action.payload
    },
    setPaymentAccount: (state, action: PayloadAction<FullCreditApplication | null>) => {
      state.current = action.payload?.credit ?? null
      state.taskList = action.payload?.tasks ?? []
    },
  },
})

export default creditSlice.reducer

export const creditActions = creditSlice.actions

export const GET_CREDIT_BY_ID = 'Applications/getById'
export const GET_CREDITS_BY_FILTERS = 'Applications/getByFilters'
export const SAVE_CREDIT = 'Applications/save'
export const GET_CURRENT_CREDIT_TASKS = 'Applications/getCurrentCreditTasks'
export const GET_CREDIT_REPORTS = 'Applications/getCreditReports'
export const SAVE_CREDIT_TASK = 'Applications/saveCreditTask'
export const SAVE_CREDIT_TASK_COMMENT = 'Applications/saveCreditTaskComment'
export const SAVE_FINAL_CREDIT_DECISION = 'Applications/SaveFinalDecision'
export const REMOVE_CREDIT_TASK = 'Applications/RemoveCreditTask'
export const CHANGE_STATUS = 'Applications/changeStatus'
export const CHANGE_RUSH = 'Applications/changeRush'
export const GET_PREVIOUS_APPLICANT_APPLICATIONS_BY_FILTER = 'previousApplicantApplications/getByFilter'
export const GET_PREVIOUS_COAPPLICANT_APPLICATIONS_BY_FILTER = 'previousCoApplicantApplications/getByFilter'
export const DOWNLOAD_CREDIT_PDF = 'Applications/downloadCreditApplicationPDF'
export const DOWNLOAD_FCF = 'Applications/downloadFCFDocument'
export const GET_CREDIT_APPLICATION_LOCK = 'Applications/getCreditApplicationLock'
export const CREATE_CREDIT_APPLICATION_LOCK = 'Applications/createCreditApplicationLock'
export const DELETE_CREDIT_APPLICATION_LOCK = 'Applications/deleteCreditApplicationLock'
export const UPDATE_CREDIT_APPLICATION_LOCK = 'Applications/updateCreditApplicationLock'
export const PAY_OUT = 'Applications/payOut'
export const GET_MESSAGE = 'Applications/getMessageList'
export const SAVE_MESSAGE = 'Applications/saveMessage'
export const CONFIRM_ALL_INCOMES = 'Applications/ConfirmAllIncomes'
export const GET_INCOMES_HISTORY = 'hystory/incomes'
export const CREATE_CVT_NUMBER = 'Applications/createCvtNumber'
export const DOWNLOAD_CVT_PDF = 'Applications/credit/downloadCvtPDF'
export const SET_REFINANCING = 'Applications/setRefinancing'
export const SET_EXISTING_APPLICANT_CUSTOMER_REFERENCE = 'Applications/setApplicantCustomerReference'
export const SET_EXISTING_COAPPLICANT_CUSTOMER_REFERENCE = 'Applications/setCoapplicantCustomerReference'
export const SET_PROOF_OF_RELEASE = 'Applications/setProofOfRelease'
export const UPDATE_INCOMES = 'Applications/updateIncomes'
export const CREATE_LOAN = 'Applications/createLoan'
export const SET_PAYMENT_ACCOUNT = 'Applications/setPaymentAccount'
export const SEND_FUND_DEPOSIT_CONFIRMATION = 'Applications/FundDepositConfirmation'
export const REFINANCE_CREDIT_APPLICATION = 'Applications/RefinanceCreditApplication'
export const COPY_CREDIT_APPLICATION_AS_DRAFT = 'Applications/CopyCreditApplicationAsDraft'
export const RESET_VOUCHED = 'Applications/ResetVouched'

const creditSelectorsPartial = {
  hasCVT: (state: TRootState) => !!state.credit.current?.cvtNumber,
  hasMerchantRateModifier: (state: TRootState) => {
    return (
      state.credit.current?.normsCreditDecision &&
      state.credit.current.normsCreditDecision.afterNormEffects.some(
        (x) => x === EAfterNormEffectsEnum.MerchantRateModifier,
      )
    )
  },
  isLoadingCreditApplication: (state: TRootState) => apiStateSelectors.isLoading(state, GET_CREDIT_BY_ID),
  isSavingCreditApplication: (state: TRootState) => apiStateSelectors.isLoading(state, SAVE_CREDIT),
  isLoadingPreviousApplicantApplication: (state: TRootState) =>
    apiStateSelectors.isLoading(state, GET_PREVIOUS_APPLICANT_APPLICATIONS_BY_FILTER),
  isLoadingPreviousCoApplicantApplication: (state: TRootState) =>
    apiStateSelectors.isLoading(state, GET_PREVIOUS_COAPPLICANT_APPLICATIONS_BY_FILTER),
  isLoadingList: (state: TRootState) => apiStateSelectors.isLoading(state, GET_CREDITS_BY_FILTERS),
  isLoadingTasks: (state: TRootState) => apiStateSelectors.isLoading(state, GET_CURRENT_CREDIT_TASKS),
  isLoadingIncomesRevision: (state: TRootState) => apiStateSelectors.isLoading(state, GET_INCOMES_HISTORY),
  isLoadingLock: (state: TRootState) => apiStateSelectors.isLoading(state, GET_CREDIT_APPLICATION_LOCK),
  getCurrent: (state: TRootState) => state.credit.current,
  hasCoapplicant: (state: TRootState) => state.credit.current?.coApplicant !== null,
  getList: (state: TRootState) => state.credit.list,
  isSavingTask: (state: TRootState) => apiStateSelectors.isLoading(state, SAVE_CREDIT_TASK),
  getCurrentCreditApplicationTasks: (state: TRootState) => state.credit.taskList,
  isSavingTaskComment: (state: TRootState) => apiStateSelectors.isLoading(state, SAVE_CREDIT_TASK_COMMENT),
  isSavingFinalDecision: (state: TRootState) => apiStateSelectors.isLoading(state, SAVE_FINAL_CREDIT_DECISION),
  isSendingFundDepositConfirmation: (state: TRootState) =>
    apiStateSelectors.isLoading(state, SEND_FUND_DEPOSIT_CONFIRMATION),
  isTogglingRush: (state: TRootState) => apiStateSelectors.isLoading(state, CHANGE_RUSH),
  getFlinksIncomesForApplicant: (state: TRootState) => {
    const flinksForApplicant = state.credit.flinksIncomes.filter((x) => x.applicantType === EApplicantType.Applicant)
    return flinksForApplicant.length > 0 ? flinksForApplicant[0] : null
  },
  getFlinksIncomesForCoApplicant: (state: TRootState) => {
    const flinksForCoApplicant = state.credit.flinksIncomes.filter(
      (x) => x.applicantType === EApplicantType.CoApplicant,
    )
    return flinksForCoApplicant.length > 0 ? flinksForCoApplicant[0] : null
  },
  getTaskListToDisplay: (state: TRootState) => {
    return state.credit.taskList.filter((x) => x.typeId !== ETaskType.ConfirmAllIncomes)
  },
  getConfirmAllIncomesStatus: (state: TRootState) => {
    return state.credit.taskList.find((x) => x.typeId === ETaskType.ConfirmAllIncomes)?.status
  },
  getCanConfirmAllIncomes: (state: TRootState) => {
    return (
      state.credit.current?.normsCreditDecision !== null &&
      state.credit.current?.normsCreditDecision?.decision !== EDecision.Refused &&
      !state.credit.taskList.some(
        (x) =>
          ((x.typeId === ETaskType.ConfirmJobIncome ||
            x.typeId === ETaskType.ConfirmOtherIncome ||
            x.typeId === ETaskType.VerifyBankruptcyHistory) &&
            x.status === ETasksStatus.ToDo) ||
          (x.typeId === ETaskType.ConfirmAllIncomes && x.status === ETasksStatus.Completed),
      ) &&
      !state.document.documentRequestedList.some(
        (x) =>
          !state.document.documentList.some(
            (y) =>
              y.status === EDocumentStatus.Approved && x.typeId === y.typeId && x.applicantType === y.applicantType,
          ) &&
          (x.requiredBeforeStep === ECreditApplicationStep.Credit ||
            x.requiredBeforeStep === ECreditApplicationStep.Financing),
      )
    )
  },
  getAllTask: (state: TRootState) => state.credit.taskList,

  canChangeLockOfCreditApplication: (state: TRootState) => {
    return (
      (state.credit.current?.finalCreditDecision.decision === EDecision.Approved &&
        (state.worksheet.current?.finalDecision?.decision === EWorksheetDecision.Approved ||
          state.personalLoanWorksheet.current?.finalDecision?.decision === EWorksheetDecision.Approved ||
          state.b2cWorksheet.current?.finalDecision?.decision === EWorksheetDecision.Approved ||
          state.productsWorksheet.current?.finalDecision.decision === EWorksheetDecision.Approved ||
          state.mechanicalWarrantyWorksheet.current?.finalDecision.decision === EWorksheetDecision.Approved) &&
        state.credit.taskList.some(
          (x) => x.typeId === ETaskType.ConfirmAllIncomes && x.status === ETasksStatus.Completed,
        ) &&
        state.document.documentList.some(
          (x) => x.typeId === EDocumentType.SignedCVT && x.status === EDocumentStatus.Approved,
        ) &&
        (state.credit.current.applicant.currentAddress.stateIso === EProvince.Quebec
          ? state.credit.current.applicant.expenses?.isCompleted
          : true)) === true && state.credit.current?.status !== ECreditApplicationStatus.Completed
    )
  },

  getPreviousApplicantApplication: (state: TRootState) => state.credit.applicantPreviousApplications,
  getPreviousCoApplicantApplication: (state: TRootState) => state.credit.coApplicantPreviousApplications,

  getIncomesHistory: (state: TRootState) => state.credit.incomesHistory,
  getDecisionHistory: (state: TRootState) => {
    const isRevised = (previousDecision: CreditDecision | null, currentDecision: CreditDecision | null) =>
      previousDecision?.decision !== currentDecision?.decision ||
      previousDecision?.interestRate !== currentDecision?.interestRate ||
      previousDecision?.maxAmountFinanced !== currentDecision?.maxAmountFinanced ||
      previousDecision?.maxPmtAmount !== currentDecision?.maxPmtAmount ||
      previousDecision?.maxTermDuration !== currentDecision?.maxTermDuration

    const historys = [] as CreditDecisionHistory[]
    let previousDecision: CreditDecision | null = null

    state.credit.decisionHistory.forEach((history) => {
      if (isRevised(previousDecision, history.finalCreditDecision)) historys.push(history)
      previousDecision = history.finalCreditDecision
    })

    return historys
  },

  getCreditApplicationLock: (state: TRootState) => state.credit.lock,
  getFinancingProgramConfigs: (state: TRootState) => {
    const programId = state.credit.current?.financingProgramId
    if (programId !== undefined) {
      return Constants.financingProgramConfigs[programId]
    }
    return defaultProgramConfig
  },

  getApplicantLaw134IsCompleted: (state: TRootState) => state.credit.current?.applicant.expenses?.isCompleted,

  getCoApplicantLaw134IsCompleted: (state: TRootState) =>
    state.credit.current?.coApplicant?.expenses?.isCompleted ?? true,

  getApplicantIsFromQc: (state: TRootState) =>
    state.credit.current?.applicant?.currentAddress?.stateIso === EProvince.Quebec,
  getCoApplicantIsFromQc: (state: TRootState) =>
    state.credit.current?.coApplicant?.currentAddress?.stateIso === EProvince.Quebec,
  getProgressionStatus: (state: TRootState) => state.credit.progressionStatus,
  getMerchant: (state: TRootState) => state.credit.merchant,
  isFinalDecisionPending: (state: TRootState) => {
    return (
      state.credit.current?.normsCreditDecision === null &&
      state.credit.current?.prequalificationDecision === null &&
      state.credit.current?.finalCreditDecision?.decision === EDecision.Pending &&
      state.credit.current?.status === ECreditApplicationStatus.Active &&
      state.credit.current.consentSoftHit &&
      state.credit.current.consentHardHit
    )
  },
  isPrequalificationPending: (state: TRootState) => {
    return (
      state.credit.current?.prequalificationDecision === null &&
      state.credit.current?.status === ECreditApplicationStatus.Active &&
      state.credit.current.consentSoftHit &&
      !state.credit.current.consentHardHit
    )
  },
  isOnlyPrequalificationDone: (state: TRootState) => {
    return (
      state.credit.current?.normsCreditDecision === null &&
      state.credit.current?.prequalificationDecision !== null &&
      state.credit.current?.consentSoftHit &&
      !state.credit.current?.consentHardHit
    )
  },
  isCreditRslaHardHitRequired: (state: TRootState) => {
    return (
      state.credit.current?.prequalificationDecision?.decision === EDecision.Approved &&
      state.credit.current?.prequalificationDecision?.isHardHitRequired
    )
  },
  isRefusedWithNoCreditReport: (state: TRootState) => {
    const prequalDecision = state.credit.current?.prequalificationDecision
    if (!prequalDecision) return false

    return (
      (prequalDecision.decision === EDecision.Refused &&
        prequalDecision.warningCodes?.includes('CoapplicantMustHaveCreditReport')) ||
      prequalDecision.warningCodes?.includes('ApplicantMustHaveCreditReport')
    )
  },
  createdWithPromotion: (state: TRootState) => state.credit.current?.merchantPaymentPlanId !== null,
  isRefinancing: (state: TRootState) => !!state.credit.current?.parentCreditApplicationId,
}

function getTranslationFromListOfKeys(
  keys: string[] | undefined,
  applicantType: EApplicantType | null,
): NormsMessage[] {
  const normsToReturn = [] as NormsMessage[]

  if (keys) {
    keys.forEach((x) => {
      normsToReturn.push({ id: x, message: x, applicantType })
    })
  }

  return normsToReturn
}

function getNormMessagesForApplicant(applicant: Applicant | null | undefined, applicantType: EApplicantType) {
  let normsList = [] as NormsMessage[]
  if (applicant?.creditDecision) {
    normsList = normsList.concat(getTranslationFromListOfKeys(applicant.creditDecision?.infoCodes, applicantType))
    normsList = normsList.concat(getTranslationFromListOfKeys(applicant.creditDecision?.warningCodes, applicantType))
    normsList = normsList.concat(
      getTranslationFromListOfKeys(applicant.creditDecision?.refusalReasonCodes, applicantType),
    )
  }

  return normsList
}

function getNormMessagesForApplication(application: CreditApplication | null | undefined) {
  let normsList = [] as NormsMessage[]
  if (application?.finalCreditDecision?.warningCodes)
    normsList = normsList.concat(getTranslationFromListOfKeys(application.finalCreditDecision.warningCodes, null))
  if (application?.finalCreditDecision?.refusalReasonCodes)
    normsList = normsList.concat(getTranslationFromListOfKeys(application.finalCreditDecision.refusalReasonCodes, null))
  return normsList
}

function allTasksCompletedForStep(step: ECreditApplicationStep, tasks: CreditApplicationTask[]) {
  return !tasks.some((x) => x.status === ETasksStatus.ToDo && x.requiredBeforeStep === step)
}
const getCurrentCreditApplicationNormsApplicant = createSelector(
  [creditSelectorsPartial.getCurrent],
  (currentCreditApplication) => {
    return getNormMessagesForApplicant(currentCreditApplication?.applicant, EApplicantType.Applicant)
  },
)
const getCurrentCreditApplicationNormsCoapplicant = createSelector(
  [creditSelectorsPartial.getCurrent],
  (currentCreditApplication) => {
    return getNormMessagesForApplicant(currentCreditApplication?.coApplicant, EApplicantType.CoApplicant)
  },
)
const getCurrentCreditApplicationSpecificNorms = createSelector(
  [creditSelectorsPartial.getCurrent],
  (currentCreditApplication) => {
    return getNormMessagesForApplication(currentCreditApplication)
  },
)

const getApplyOnlineLink = createSelector([creditSelectorsPartial.getCurrent], (creditApp) => {
  let baseUrl = null as string | null
  switch (creditApp?.financingProgramId) {
    case EFinancingProgram.Personal:
      if (creditApp.loanPurposeId === ELoanPurpose.Medical || creditApp.loanPurposeId === ELoanPurpose.Aesthetics) {
        baseUrl = import.meta.env.VITE_APPLY_MEDICARD_URL
      } else {
        baseUrl = import.meta.env.VITE_APPLY_PERSONAL_LOAN_URL
      }
      break

    case EFinancingProgram.B2c:
      if (creditApp.merchantId === EStaticMerchantIds.CIRCLEK_B2C_MERCHANT_ID) {
        baseUrl = import.meta.env.VITE_APPLY_CIRCLEK_URL
      } else if (creditApp.merchantId === EStaticMerchantIds.NEO_B2C_MERCHANT_ID) {
        baseUrl = import.meta.env.VITE_APPLY_NEO_URL
      } else {
        baseUrl = import.meta.env.VITE_APPLY_B2C_URL
      }
      break

    case EFinancingProgram.MechanicalWarranty:
      baseUrl = `${import.meta.env.VITE_APPLY_MECH_WARRANTY_URL}`
      break

    default:
      baseUrl = null
      break
  }

  if (baseUrl && creditApp) {
    return `${baseUrl}/creditApplication/${creditApp.id}`
  }
  return null
})

export const creditSelectors = {
  ...creditSelectorsPartial,
  getCurrentCreditApplicationNormsApplicant,
  getCurrentCreditApplicationNormsCoapplicant,
  getCurrentCreditApplicationSpecificNorms,
  getApplyOnlineLink,
  areAllTasksRequiredForCreditCompleted: createSelector([creditSelectorsPartial.getAllTask], (tasks) => {
    return allTasksCompletedForStep(ECreditApplicationStep.Credit, tasks)
  }),
  areAllTasksRequiredForCVTCompleted: createSelector([creditSelectorsPartial.getAllTask], (tasks) => {
    return (
      allTasksCompletedForStep(
        ECreditApplicationStep.CVT,
        tasks.filter((x) => x.typeId !== ETaskType.ApproveWorksheet), // Without approve cvt task
      ) &&
      allTasksCompletedForStep(ECreditApplicationStep.Financing, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.Credit, tasks)
    )
  }),
  areAllTasksRequiredForPayoutCompleted: createSelector([creditSelectorsPartial.getAllTask], (tasks) => {
    return (
      allTasksCompletedForStep(ECreditApplicationStep.PayOut, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.CVT, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.Credit, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.Financing, tasks)
    )
  }),
  getCurrentCreditApplicationNorms: createSelector(
    [
      getCurrentCreditApplicationNormsApplicant,
      getCurrentCreditApplicationNormsCoapplicant,
      getCurrentCreditApplicationSpecificNorms,
    ],
    (applicantNormsMessages, coApplicantNormsMessage, currentCreditNorms) => {
      let normsList = [] as NormsMessage[]
      normsList = applicantNormsMessages.concat(coApplicantNormsMessage).concat(currentCreditNorms)

      return normsList
    },
  ),
  getPreviousApplicantApplication: createSelector(
    [creditSelectorsPartial.getPreviousApplicantApplication, creditSelectorsPartial.getCurrent],
    (PreviousApplicantApplication, currentCreditApplication) => {
      return PreviousApplicantApplication.filter((x) => x.creditApplicationId !== currentCreditApplication?.id)
    },
  ),
  getPreviousCoApplicantApplication: createSelector(
    [creditSelectorsPartial.getPreviousCoApplicantApplication, creditSelectorsPartial.getCurrent],
    (PreviousCoApplicantApplication, currentCreditApplication) => {
      return PreviousCoApplicantApplication.filter((x) => x.creditApplicationId !== currentCreditApplication?.id)
    },
  ),
  getMerchantPaymentPlan: createSelector(
    [creditSelectorsPartial.getCurrent, personalLoanWorksheetSelectors.getCurrent, creditSelectorsPartial.getMerchant],
    (creditApplication, personalLoanWorksheet, merchant) => {
      const paymentPlanId = personalLoanWorksheet?.paymentPlanId ?? creditApplication?.merchantPaymentPlanId
      if (paymentPlanId && merchant && merchant.paymentPlans?.length > 0) {
        return merchant.paymentPlans.find((p) => p.id === paymentPlanId)
      }
      return null
    },
  ),
  isAbleToPrintCvt: createSelector(
    [
      creditSelectorsPartial.getCurrent,
      autoworksheetSelectors.getCurrent,
      personalLoanWorksheetSelectors.getCurrent,
      b2cWorksheetSelectors.getCurrent,
      productsWorkSheetSelectors.getCurrent,
      ifXpressWorksheetSelectors.getCurrent,
      mechanicalWarrantyWorksheetSelectors.getCurrent,
      creditSelectorsPartial.getApplicantLaw134IsCompleted,
      creditSelectorsPartial.getCoApplicantLaw134IsCompleted,
      creditSelectorsPartial.getApplicantIsFromQc,
      creditSelectorsPartial.getCoApplicantIsFromQc,
    ],
    (
      creditApplication,
      autoWorksheet,
      personalLoanWorksheet,
      b2cWorksheet,
      productsWorksheet,
      ifXpressWorksheet,
      mechanicalWarrantyWorksheet,
      applicantLaw134IsCompleted,
      coApplicantLaw134IsCompleted,
      applicantIsFromQc,
      coApplicantIsFromQc,
    ) => {
      const worksheet: BaseWorksheet | null =
        autoWorksheet ??
        personalLoanWorksheet ??
        b2cWorksheet ??
        productsWorksheet ??
        ifXpressWorksheet ??
        mechanicalWarrantyWorksheet
      return (
        (creditApplication?.finalCreditDecision?.decision === EDecision.Approved &&
          worksheet?.finalDecision?.decision === EWorksheetDecision.Approved &&
          (!applicantIsFromQc || applicantLaw134IsCompleted) &&
          (!coApplicantIsFromQc || coApplicantLaw134IsCompleted)) === true
      )
    },
  ),
  canContinuePersonnalLoanFinancing: createSelector(
    [
      creditSelectorsPartial.getCurrent,
      personalLoanWorksheetSelectors.getCurrent,
      availableCreditSelectors.getAvailableCredit,
      (state: TRootState) => appSelectors.getFinancingConfig(state, EFinancingProgram.Personal),
    ],
    (creditApplication, personalLoanWorksheet, availableCredit, financingConfig) => {
      const today = new Date()
      const expiresOn = new Date(creditApplication?.expiresOn ?? '')
  
      const reasons: EContinueFinancingDisableReason[] = []

      if ((availableCredit ?? 0) - (personalLoanWorksheet?.amountRequested ?? 0) < financingConfig.minimumLoanAmount)
        reasons.push(EContinueFinancingDisableReason.MaximumAvalaibleAmountIsTooLow)

      if (expiresOn <= today) reasons.push(EContinueFinancingDisableReason.ExpiresOnIsPassed)

      if (personalLoanWorksheet?.finalDecision.decision !== EWorksheetDecision.Approved)
        reasons.push(EContinueFinancingDisableReason.WorksheetIsNotApproved)

      if (creditApplication?.parentReferenceNumber !== null && creditApplication?.loanId == null)
        reasons.push(EContinueFinancingDisableReason.CantRefinanceUntilDone)
      return reasons
    },
  ),
  getCurrentWorksheetNormWarningMessages: createSelector(
    [
      autoworksheetSelectors.getCurrentWorksheetNormRefusalAndWarningMessages,
      personalLoanWorksheetSelectors.getCurrentWorksheetNormRefusalAndWarningMessages,
      b2cWorksheetSelectors.getCurrentWorksheetNormRefusalAndWarningMessages,
      productsWorkSheetSelectors.getCurrentWorksheetNormRefusalAndWarningMessages,
      autoRslaWorksheetSelectors.getCurrentWorksheetNormRefusalAndWarningMessages,
      creditRslaWorksheetSelectors.getCurrentWorksheetNormRefusalAndWarningMessages,
    ],
    (
      autoWorksheetNormsRefusalAndWarning,
      personalLoanWorksheetNormsRefusalAndWarning,
      b2cWorksheetNormsRefusalAndWarning,
      productsWorksheetNormsRefusalAndWarning,
      autoRslaWorksheetNormsRefusalAndWarning,
      creditRslaWorksheetNormsRefusalAndWarning,
    ) => {
      let normsList = [] as NormsMessage[]

      normsList = [
        ...autoWorksheetNormsRefusalAndWarning,
        ...personalLoanWorksheetNormsRefusalAndWarning,
        ...b2cWorksheetNormsRefusalAndWarning,
        ...productsWorksheetNormsRefusalAndWarning,
        ...autoRslaWorksheetNormsRefusalAndWarning,
        ...creditRslaWorksheetNormsRefusalAndWarning,
      ].filter(Boolean)

      return normsList
    },
  ),
}
